import { motion } from 'framer-motion'
import { 
  Users, 
  Briefcase, 
  Image, 
  Calendar,
  TrendingUp,
  FileText,
  Settings,
  LogOut,
  Plus,
  Eye,
  UserPlus
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import { Link } from 'react-router-dom'
import GlassCard from '../../components/ui/GlassCard'
import AdminLayout from '../../components/admin/AdminLayout'

const Dashboard = () => {
  const { 
    user, 
    logout, 
    jobPostings, 
    applications, 
    galleryImages 
  } = useStore()

  const stats = [
    {
      title: 'Offres d\'Emploi',
      value: jobPostings.filter(j => j.status === 'active').length,
      total: jobPostings.length,
      icon: Briefcase,
      color: 'from-green-500 to-emerald-500',
      link: '/admin/jobs'
    },
    {
      title: 'Candidatures',
      value: applications.length,
      total: applications.filter(a => a.status === 'new').length,
      icon: Users,
      color: 'from-purple-500 to-pink-500',
      link: '/admin/applications'
    },
    {
      title: 'Photos Galerie',
      value: Object.values(galleryImages).flat().length,
      total: Object.values(galleryImages).flat().length,
      icon: Image,
      color: 'from-orange-500 to-red-500',
      link: '/admin/gallery'
    }
  ]

  const quickActions = [
    {
      title: 'Gérer Offres',
      description: 'Publier et gérer les offres d\'emploi',
      icon: Briefcase,
      link: '/admin/jobs',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Gérer Galeries',
      description: 'Ajouter et organiser les photos',
      icon: Image,
      link: '/admin/gallery',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Paramètres',
      description: 'Configuration générale',
      icon: Settings,
      link: '/admin/settings',
      color: 'from-gray-500 to-slate-500'
    },
    {
      title: 'Gérer Admins',
      description: 'Ajouter de nouveaux administrateurs',
      icon: UserPlus,
      link: '/admin/admins',
      color: 'from-indigo-500 to-purple-500'
    }
  ]

  const recentActivities = []  // Données réelles uniquement, pas de données pré-remplies

  return (
    <AdminLayout>
      <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold gradient-text">
                Tableau de Bord Admin
              </h1>
              <p className="text-gray-600 mt-1">
                Bienvenue, {user?.name}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={logout}
              className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={stat.link}>
                  <GlassCard className="p-6 hover:scale-105 transition-transform cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                        <div className="flex items-baseline space-x-2">
                          <span className="text-2xl font-bold text-black">
                            {stat.value}
                          </span>
                          <span className="text-sm text-gray-500">
                            / {stat.total}
                          </span>
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            )
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2"
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-black mb-6">
                Actions Rapides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const Icon = action.icon
                  return (
                    <Link key={action.title} to={action.link}>
                      <motion.div
                        whileHover={{ scale: 1.02, y: -2 }}
                        className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${action.color}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-black">
                              {action.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {action.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  )
                })}
              </div>
            </GlassCard>
          </motion.div>

          {/* Recent Activities */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-black mb-6">
                Activité Récente
              </h2>
              <div className="space-y-4">
                {recentActivities.map((activity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        {activity.message}
                      </p>
                      <p className="text-xs text-gray-500">
                        Il y a {activity.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
      </div>
    </AdminLayout>
  )
}

export default Dashboard
